import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { Check, LockKeyhole, SlidersHorizontal, X } from "lucide-react";
import { Switch } from "../ui/switch";
import { useConsent } from "../../lib/consentContext";
import "./CookieConsent.css";

const ChoiceRow = ({ icon, title, description, children, testId }) => (
  <div className="cookie-choice" data-testid={testId}>
    <span className="cookie-choice-icon" aria-hidden="true">{icon}</span>
    <span className="cookie-choice-copy"><strong>{title}</strong><small>{description}</small></span>
    {children}
  </div>
);

const PreferencesDialog = () => {
  const { consent, preferencesOpen, closePreferences, restoreFocus, saveConsent } = useConsent();
  const [analytics, setAnalytics] = useState(consent?.choices.analytics ?? false);

  useEffect(() => {
    if (preferencesOpen) setAnalytics(consent?.choices.analytics ?? false);
  }, [consent, preferencesOpen]);

  return (
    <Dialog.Root open={preferencesOpen} onOpenChange={(open) => !open && closePreferences()}>
      <Dialog.Portal>
        <Dialog.Overlay className="cookie-modal-overlay" />
        <Dialog.Content className="cookie-modal" data-testid="cookie-preferences-modal" onCloseAutoFocus={(event) => { event.preventDefault(); restoreFocus(); }}>
          <div className="cookie-modal-heading">
            <span className="cookie-mark"><SlidersHorizontal size={17} /></span>
            <div>
              <p className="cookie-label">Privacy controls</p>
              <Dialog.Title>Cookie preferences</Dialog.Title>
            </div>
          </div>
          <Dialog.Description className="cookie-modal-description">
            Choose whether HUMN may collect optional, first-party website analytics. You can change this anytime.
          </Dialog.Description>

          <div className="cookie-choices">
            <ChoiceRow icon={<LockKeyhole size={17} />} title="Strictly necessary" description="Security, theme and your consent choice. Always active." testId="cookie-essential-choice">
              <span className="cookie-always-on" data-testid="cookie-essential-status"><Switch checked disabled aria-label="Strictly necessary cookies are always active" data-testid="cookie-toggle-essential" className="cookie-switch" /><Check size={14} /> Always on</span>
            </ChoiceRow>
            <ChoiceRow icon={<SlidersHorizontal size={17} />} title="Analytics" description="Helps us understand page usage and improve the website." testId="cookie-analytics-choice">
              <Switch checked={analytics} onCheckedChange={setAnalytics} aria-label="Allow analytics" data-testid="cookie-toggle-analytics" className="cookie-switch" />
            </ChoiceRow>
          </div>

          <div className="cookie-modal-actions">
            <Link to="/cookie-policy" onClick={closePreferences} className="cookie-policy-link" data-testid="cookie-modal-policy-link">Read Cookie Policy</Link>
            <button type="button" onClick={() => saveConsent(analytics)} className="cookie-primary-btn" data-testid="cookie-save-preferences-btn">Save choices</button>
          </div>
          <Dialog.Close asChild>
            <button type="button" className="cookie-modal-close" aria-label="Close cookie preferences" data-testid="cookie-modal-close-btn"><X size={18} /></button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export const CookieConsent = () => {
  const { consent, openPreferences, saveConsent } = useConsent();

  return (
    <>
      {!consent && (
        <section className="cookie-banner" role="region" aria-label="Cookie consent notice" data-testid="cookie-consent-banner">
          <div className="cookie-banner-copy">
            <p className="cookie-label">Your privacy, your choice</p>
            <h2 data-testid="cookie-banner-title">Cookies on HUMN</h2>
            <p data-testid="cookie-banner-description">We use necessary browser storage to keep this site working. Optional analytics help us improve the experience.</p>
            <Link to="/cookie-policy" className="cookie-policy-link" data-testid="cookie-banner-policy-link">Read Cookie Policy</Link>
          </div>
          <div className="cookie-banner-actions">
            <button type="button" onClick={() => saveConsent(false)} className="cookie-secondary-btn" data-testid="cookie-necessary-only-btn">Necessary only</button>
            <button type="button" onClick={(event) => openPreferences(event.currentTarget)} className="cookie-secondary-btn" data-testid="cookie-manage-preferences-btn">Manage</button>
            <button type="button" onClick={() => saveConsent(true)} className="cookie-primary-btn" data-testid="cookie-accept-all-btn">Accept all</button>
          </div>
        </section>
      )}
      <PreferencesDialog />
    </>
  );
};